import * as React from "react";
import * as ReactDOM from "react-dom";
import { getDenominations } from "../../../api/denominations";
import { IDenomination } from "../../../models/get";
import { Loader, LoaderStyle } from "../../../components/loader";
import { ToolBar, ToolBarItem, ToolBarSeparator } from "../../../components/toolbar";
import { InputSize } from "../../../components/input";
import { TextBox } from "../../../components/textBox";
import { Button, ButtonType, ButtonSize } from "../../../components/button";
import { Table, ITableColumn, ITableRowEventArgs } from "../../../components/table";
import SettingsNav from "../nav";
import Dialog from "./dialog";

interface IState
{
    /**
     * Gibt an, ob gerade geladen wird.
     */
    isLoading?: boolean,

    /**
     * Gibt an, ob der Dialog geöffnet ist.
     */
    isDialogOpen?: boolean,

    /**
     * Gibt an, ob der Dialog zum bearbeiten eines Datensatzes geöffnet wird.
     */
    isDialogInEditMode?: boolean,

    /**
     * Enthält Text der zum Filtern der Liste verwendet wird.
     */
    filterValue?: string,

    /**
     * Enthält die geladenen Daten.
     */
    denominations?: IDenomination[],

    /**
     * Enthält die gefilterten Daten.
     */
    filteredDenominations?: IDenomination[],

    /**
     * Enthält den momentan selektierten Datensatz.
     */
    selectedItem?: IDenomination,

    /**
     * Enthält den momentan selektierten Zeilenindex.
     */
    selectedRowIndex?: string
}

export class DenominationSettings extends React.Component<{}, IState>
{
    /**
     * Erstellt eine neue Instanz der Klasse @see DenominationSettings.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props)
    {
        super(props);

        this.state = {
            isLoading: true
        }

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props)
    {
        var denominations = await getDenominations();

        this.setState({
            isLoading: false,
            denominations: denominations,
            filteredDenominations: denominations
        });
    }

    /**
     * Aktualisiert die Liste.
     */
    private async refreshAsync()
    {
        this.setState({
            isLoading: true,
            filterValue: null
        });

        await this.initAsync(this.props);
    }

    /**
     * Wird ausgeführt, sobald sich der Text der Filter TextBox ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     */
    private onFilterTextChanged = (value: any[], originalValue: string[]): void =>
    {
        const { denominations } = this.state;
        var filter: string = value[0];
        var filteredData: IDenomination[] = [];

        if (filter != null && filter !== "")
            filteredData = denominations.where(s => s.denomination.toLowerCase().indexOf(filter.toLowerCase()) >= 0);
        else
            filteredData = denominations;

        this.setState({
            filterValue: value[0],
            filteredDenominations: filteredData
        });
    }

    /**
     * Wird ausgeführt sobald auf eine Zeile geklickt wird. Die Daten der ausgewählten
     * Zeile werden hier für weitere Zwecke zwischengespeichert.
     * @param e
     */
    private onRowClick = (e: ITableRowEventArgs<any>) =>
    {
        this.setState({
            selectedItem: e.dataSource,
            selectedRowIndex: e.index
        });
    }

    /**
     * Öffnet den Dialog zum erstellen eines neuen Datensatzes.
     */
    private onNewClick = () =>
    {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: false
        });
    }

    /**
     * Öffnet den Dialog zum bearbeiten eines Datensatzes.
     */
    private onEditClick = () =>
    {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: true
        });
    }

    /**
     * Wird aufgerufen, sobald sich der Dialog schließt.
     * @param refresh Angabe, ob die Ansicht aktualisiert werden soll.
     */
    private onDialogClosed = async (refresh: boolean) =>
    {
        if (refresh)
        {
            await this.refreshAsync();
            this.setState({
                isDialogOpen: false,
                selectedItem: null,
                selectedRowIndex: null
            });
        }
        else
        {
            this.setState({
                isDialogOpen: false
            });
        }
    }

    /**
     * Render einen InfoText wenn die Liste leer ist.
     */
    private renderInfoText(): JSX.Element
    {
        return (
            <div className="settings-default_empty">
                <i className="settings-default_empty_icon mdi mdi-clipboard-account"></i>
                <div className="settings-default_empty_header">Konfessionen</div>
                <div
                    style={{
                        width: 370
                    }}
                    className="settings-default_empty_text">
                    Erstellen Sie Konfessionen um verstorbene Personen besser zu differenzieren.
                </div>
                <Button
                    icon="plus"
                    text="Konfession"
                    type={ButtonType.primary}
                    size={ButtonSize.big}
                    onClick={this.onNewClick} />
            </div>
        );
    }

    /**
     * Rendert die Tabelle.
     */
    private renderTable(): JSX.Element
    {
        const { denominations, filteredDenominations } = this.state;
        const columns: ITableColumn[] = [
            {
                header: "Konfession",
                accessor: "denomination",
                width: 200
            }
        ];

        if (denominations != null && denominations.length > 0)
        {
            return (
                <Table
                    style={{
                        position: "relative"
                    }}
                    dataSource={filteredDenominations}
                    columns={columns}
                    keyAccessor="id"
                    onRowClick={this.onRowClick} />
            );
        }
        else
        {
            return this.renderInfoText();
        }
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const { isLoading, isDialogOpen, isDialogInEditMode, filterValue, denominations, selectedItem } = this.state;
        const isListEmpty: boolean = denominations == null || denominations.length === 0;
        const isItemSelected: boolean = selectedItem != null;

        if (isLoading)
        {
            return (
                <Loader
                    style={{ marginTop: 40 }}
                    loaderStyle={LoaderStyle.style1}
                    infoText="Konfessionen werden geladen" />
            );
        }

        return (
            <div className="settings-content">
                <div className="settings-content_container">
                    <div className="settings-content_nav">
                        <SettingsNav selected="denominations" />
                    </div>
                    <div className="settings-default-container">
                        {
                            !isListEmpty &&
                            <div className="settings-default_toolbar">
                                <ToolBar hasTopBorder={false}>
                                    <ToolBarItem
                                        icon="plus"
                                        text="Neu"
                                        onClick={this.onNewClick} />
                                    <ToolBarSeparator />
                                    <ToolBarItem
                                        icon="pencil"
                                        text="Bearbeiten"
                                        onClick={this.onEditClick}
                                        isDisabled={!isItemSelected} />
                                </ToolBar>
                            </div>
                        }
                        {
                            !isListEmpty &&
                            <div className="settings-default_filter">
                                <TextBox
                                    placeholder="Filter Konfession..."
                                    size={InputSize.normal}
                                    value={filterValue}
                                    onTextChange={this.onFilterTextChanged} />

                            </div>
                        }
                        <div className="settings-default">
                            {this.renderTable()}
                        </div>
                        {
                            isDialogOpen &&
                            <Dialog
                                denominationId={isDialogInEditMode && isItemSelected && selectedItem.id}
                                onClose={this.onDialogClosed} />
                        }
                        </div>
                </div>
            </div>
        );
    }
}